import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Storefront, Eye, EyeSlash, ArrowLeft, MapPin, EnvelopeSimple, User, At } from '@phosphor-icons/react';
import { Logo } from '../components/Logo';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ShopRegisterPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    shop_name: '', city: '', state: '',
    name: '', username: '', email: '', password: '', confirm: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    // Light client-side validation — backend re-validates everything.
    if (!form.shop_name.trim()) return setError('Shop name is required');
    if (!form.city.trim()) return setError('City is required');
    if (form.password !== form.confirm) return setError('Passwords do not match');
    if (form.password.length < 6) return setError('Password must be at least 6 characters');
    if (form.username.length < 3) return setError('Username must be at least 3 characters');

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/register-as-shop`, {
        email: form.email,
        password: form.password,
        username: form.username,
        name: form.name,
        shop_name: form.shop_name,
        city: form.city,
        state: form.state,
      }, { withCredentials: true });

      // The endpoint sets HTTP-only cookies AND returns tokens for the native
      // bearer-token fallback (iOS). Persist for native; web uses cookies.
      if (data.access_token) {
        try { localStorage.setItem('access_token', data.access_token); } catch {}
      }
      // Land directly on the shop welcome screen (3 CTAs → dashboard).
      navigate(data.redirect_to || `/shop-welcome/${data.shop_id}`, { replace: true });
      // Force a page reload so AuthContext picks up the new session.
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'Shop signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inp = "w-full bg-[#0e0e16] border border-white/[0.08] rounded-xl px-4 py-3 text-white text-sm placeholder:text-[#444] focus:outline-none focus:border-[#9D4CDD]/50 focus:bg-[#0e0e16]";

  return (
    <div className="app-container flex flex-col items-center justify-center min-h-screen p-6" data-testid="shop-register-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">

        <button onClick={() => navigate('/register')} className="text-[#A1A1AA] text-xs mb-4 inline-flex items-center gap-1 hover:text-white"
          data-testid="back-to-register">
          <ArrowLeft size={12} weight="bold" /> Back
        </button>

        <div className="flex flex-col items-center mb-6">
          <Logo variant="full" size="lg" clickable={false} showTagline={false} />
          <div className="flex items-center gap-2 mt-4">
            <Storefront size={18} weight="duotone" className="text-[#9D4CDD]" />
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#9D4CDD] font-semibold">Shop Account</p>
          </div>
          <h1 className="text-white text-xl font-bold mt-1">Register your game shop</h1>
          <p className="text-[#666] text-xs mt-1 text-center">Set up your shop in 60 seconds. We'll ask for the rest after.</p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          {/* SHOP IDENTITY */}
          <div className="bg-gradient-to-br from-[#9D4CDD]/8 via-[#0e0e16] to-[#0e0e16] border border-[#9D4CDD]/10 rounded-2xl p-3 space-y-3">
            <p className="text-[10px] uppercase tracking-wider text-[#9D4CDD] font-semibold">Your shop</p>
            <input className={inp} placeholder="Shop name (e.g. The Game Den)"
              value={form.shop_name} onChange={set('shop_name')} data-testid="shop-name-input" />
            <div className="grid grid-cols-3 gap-2">
              <input className={`${inp} col-span-2`} placeholder="City"
                value={form.city} onChange={set('city')} data-testid="shop-city-input" />
              <input className={inp} placeholder="State"
                value={form.state} onChange={set('state')} maxLength={2} data-testid="shop-state-input" />
            </div>
          </div>

          {/* OWNER IDENTITY */}
          <div className="bg-[#0e0e16] border border-white/[0.06] rounded-2xl p-3 space-y-3">
            <p className="text-[10px] uppercase tracking-wider text-[#888] font-semibold">Owner login</p>
            <div className="relative">
              <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
              <input className={`${inp} pl-9`} placeholder="Your name"
                value={form.name} onChange={set('name')} data-testid="owner-name-input" />
            </div>
            <div className="relative">
              <At size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
              <input className={`${inp} pl-9`} placeholder="Username" autoComplete="off"
                value={form.username} onChange={set('username')} data-testid="owner-username-input" />
            </div>
            <div className="relative">
              <EnvelopeSimple size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
              <input className={`${inp} pl-9`} placeholder="Email" type="email" autoComplete="email"
                value={form.email} onChange={set('email')} data-testid="owner-email-input" />
            </div>
            <div className="relative">
              <input className={inp} placeholder="Password (min 6 chars)" type={showPassword ? 'text' : 'password'}
                value={form.password} onChange={set('password')} data-testid="owner-password-input" />
              <button type="button" onClick={() => setShowPassword(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555] hover:text-white"
                aria-label="Toggle password visibility">
                {showPassword ? <EyeSlash size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <input className={inp} placeholder="Confirm password" type={showPassword ? 'text' : 'password'}
              value={form.confirm} onChange={set('confirm')} data-testid="owner-confirm-input" />
          </div>

          {error && (
            <div className="text-[#EF4444] text-xs bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-lg p-2" data-testid="shop-register-error">
              {error}
            </div>
          )}

          <button type="submit" disabled={loading}
            className="btn-primary w-full mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="shop-register-submit">
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Creating shop…
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2"><Storefront size={16} weight="duotone" /> Create Shop Account</span>
            )}
          </button>
        </form>

        <p className="text-center mt-5 text-[#A1A1AA] text-sm">
          Existing shop owner?{' '}
          <Link to="/login" className="text-[#00F0FF] hover:underline" data-testid="shop-existing-login">
            Sign in
          </Link>
        </p>

        <p className="text-center mt-4 text-[10px] text-[#555]">
          By creating an account you agree to our{' '}
          <Link to="/terms" className="text-[#777] hover:text-[#9D4CDD] underline">Terms</Link>
          {' '}and{' '}
          <Link to="/privacy" className="text-[#777] hover:text-[#9D4CDD] underline">Privacy Policy</Link>.
        </p>
      </motion.div>
    </div>
  );
}
